/**
 * DataMigrationCard — Settings card to copy legacy seeUwell forms and submissions
 * into ClearIntake (Phase 1).
 *
 * - Scope selector: Forms + Submissions / Forms only / Submissions only.
 * - Optional cross-account import (only shown when the server enables it): preview
 *   and import from a DIFFERENT account by its GHL Location ID. Rows are always
 *   written to THIS tenant; the source only changes what's read.
 *
 * Only ever reads FROM seeUwell (read-only); clearing removes only this tenant's
 * migrated rows.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { migration as migrationApi, MigrationStatus } from '../../../lib/api.js';
import { useAuth } from '../../../lib/auth-context.js';

type Scope = 'both' | 'forms' | 'submissions' | 'messages';

const SCOPES: Array<{ value: Scope; label: string }> = [
  { value: 'both',        label: 'Forms + Submissions' },
  { value: 'forms',       label: 'Forms only' },
  { value: 'submissions', label: 'Submissions only' },
  { value: 'messages',    label: 'Messages' },
];

export default function DataMigrationCard(): React.ReactElement | null {
  const { isAgencyAdmin } = useAuth();
  const [status,    setStatus]    = useState<MigrationStatus | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [running,   setRunning]   = useState(false);
  const [clearing,  setClearing]  = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [scope,     setScope]     = useState<Scope>('both');
  const [sourceInput, setSourceInput] = useState('');
  const [msg,       setMsg]       = useState<string | null>(null);
  const [error,     setError]     = useState<string | null>(null);

  const load = useCallback(async (source?: string) => {
    setLoading(true); setError(null);
    try {
      const res = await migrationApi.status(source);
      if (res.success && res.data) setStatus(res.data);
      else setError(res.error ?? 'Failed to load migration status');
    } catch {
      setError('Failed to load migration status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Only agency admins may reach the migration endpoints (server-gated with a
  // 403); skip the request entirely for anyone else so no error banner flashes.
  useEffect(() => { if (isAgencyAdmin) void load(); }, [load, isAgencyAdmin]);

  // Data Migration is an agency-admin-only surface. Location admins can open
  // Settings, so the card hides itself rather than showing a dead 403 control.
  if (!isAgencyAdmin) return null;

  const activeSource = status?.source ?? null; // the source currently previewed (null = own account)
  const isCross = Boolean(activeSource);

  const preview = () => { const s = sourceInput.trim(); if (s) { setMsg(null); void load(s); } };
  const backToOwn = () => { setSourceInput(''); setMsg(null); void load(); };

  const handleRun = async () => {
    setRunning(true); setError(null); setMsg(null);
    try {
      const opts: { forms: boolean; submissions: boolean; messages: boolean; sourceLocationId?: string } = {
        forms: scope === 'both' || scope === 'forms',
        submissions: scope === 'both' || scope === 'submissions',
        messages: scope === 'messages',
      };
      if (activeSource) opts.sourceLocationId = activeSource;
      const res = await migrationApi.run(opts);
      if (res.success && res.data) {
        const f = res.data.forms, s = res.data.submissions, m = res.data.messages;
        const parts: string[] = [];
        if (f && (scope !== 'submissions' || f.created > 0)) {
          parts.push(`${f.created} form${f.created === 1 ? '' : 's'} copied${f.skipped ? `, ${f.skipped} already present` : ''}`);
        }
        if (s) {
          parts.push(`${s.created} submission${s.created === 1 ? '' : 's'} copied${s.skipped ? `, ${s.skipped} already present` : ''}${s.skippedNoForm ? `, ${s.skippedNoForm} skipped (no matching form)` : ''}`);
        }
        if (m) {
          parts.push(`${m.created} message${m.created === 1 ? '' : 's'} copied${m.skipped ? `, ${m.skipped} already present` : ''}${m.skippedNoContact ? `, ${m.skippedNoContact} skipped (no matching contact)` : ''}`);
        }
        setMsg(parts.join(' · ') || 'Nothing new to copy.');
        await load(activeSource ?? undefined);
      } else {
        setError(res.error ?? 'Migration failed');
      }
    } catch {
      setError('Migration failed');
    } finally {
      setRunning(false);
    }
  };

  const handleClear = async () => {
    setClearing(true); setError(null); setMsg(null);
    try {
      // Clear the SAME scope selected above. Forms imply their submissions (FK); messages
      // are cleared on their own.
      const res = await migrationApi.clear({
        forms:       scope === 'both' || scope === 'forms',
        submissions: scope === 'both' || scope === 'forms' || scope === 'submissions',
        messages:    scope === 'messages',
      });
      if (res.success && res.data) {
        const d = res.data;
        const parts: string[] = [];
        if (d.formsDeleted)       parts.push(`${d.formsDeleted} form${d.formsDeleted === 1 ? '' : 's'}`);
        if (d.submissionsDeleted) parts.push(`${d.submissionsDeleted} submission${d.submissionsDeleted === 1 ? '' : 's'}`);
        if (d.messagesDeleted)    parts.push(`${d.messagesDeleted} message${d.messagesDeleted === 1 ? '' : 's'}`);
        setMsg(parts.length ? `Cleared ${parts.join(', ')}.` : 'Nothing to clear for that scope.');
        setConfirmClear(false);
        await load();
      } else {
        setError(res.error ?? 'Clear failed');
      }
    } catch {
      setError('Clear failed');
    } finally {
      setClearing(false);
    }
  };

  const c = status?.counts;
  const migratedTotal = c ? c.migratedForms + c.migratedSubmissions + c.migratedMessages : 0;
  // Clear acts on the SAME scope as the selector; forms include their submissions.
  const clearTargetLabel = scope === 'messages' ? 'messages'
    : scope === 'submissions' ? 'submissions'
    : 'forms & submissions';
  const scopeMigratedCount = !c ? 0
    : scope === 'messages' ? c.migratedMessages
    : scope === 'submissions' ? c.migratedSubmissions
    : c.migratedForms + c.migratedSubmissions;
  const runLabel = running
    ? 'Copying…'
    : migratedTotal > 0
      ? 'Copy new items'
      : `Migrate ${scope === 'both' ? 'forms & submissions' : scope}`;

  return (
    <div style={styles.card}>
      <h2 style={styles.cardTitle}>Data Migration</h2>
      <p style={styles.help}>
        Copy existing forms and form submissions from the legacy seeUwell system into ClearIntake.
        Your seeUwell data is never modified — this only reads from it.
      </p>

      {loading && <p style={styles.muted}>Checking for legacy data…</p>}

      {!loading && status && !status.configured && (
        <p style={styles.muted}>Legacy migration isn&apos;t enabled for this environment yet.</p>
      )}

      {/* Cross-account source (only when the server enables it) */}
      {!loading && status?.configured && status.crossAccountEnabled && (
        <div style={styles.sourceBox}>
          <label style={styles.sourceLabel}>Import from a different account (Location ID)</label>
          <div style={styles.sourceRow}>
            <input
              style={styles.sourceInput}
              value={sourceInput}
              onChange={(e) => setSourceInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') preview(); }}
              placeholder="Leave blank for your own account"
              disabled={running || clearing}
            />
            <button style={styles.secondaryBtn} onClick={preview} disabled={running || clearing || !sourceInput.trim()}>Preview</button>
            {isCross && <button style={styles.linkBtn} onClick={backToOwn} disabled={running || clearing}>My account</button>}
          </div>
          {isCross && <p style={styles.sourceNote}>Previewing source account <code>{activeSource}</code> → importing into your account.</p>}
        </div>
      )}

      {!loading && status?.configured && !status.mapped && (
        <p style={styles.muted}>
          No legacy seeUwell account is linked to {isCross ? 'that account id' : 'this location'}, so there&apos;s nothing to migrate.
        </p>
      )}

      {!loading && status?.mapped && c && (
        <>
          {/* Scope selector */}
          <div style={styles.segment}>
            {SCOPES.map((s) => (
              <button
                key={s.value}
                style={{ ...styles.segBtn, ...(scope === s.value ? styles.segBtnActive : {}) }}
                onClick={() => setScope(s.value)}
                disabled={running || clearing}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div style={styles.statRow}>
            <div style={styles.stat}>
              <span style={styles.statNum}>{c.migratedForms}<span style={styles.statTotal}> / {c.legacyForms}</span></span>
              <span style={styles.statLabel}>Forms migrated</span>
            </div>
            <div style={styles.stat}>
              <span style={styles.statNum}>{c.migratedSubmissions}<span style={styles.statTotal}> / {c.legacySubmissions}</span></span>
              <span style={styles.statLabel}>Submissions migrated</span>
            </div>
            <div style={styles.stat}>
              <span style={styles.statNum}>{c.migratedMessages}<span style={styles.statTotal}> / {c.legacyMessages}</span></span>
              <span style={styles.statLabel}>Messages migrated</span>
            </div>
          </div>

          <div style={styles.btnRow}>
            <button style={{ ...styles.primaryBtn, opacity: running ? 0.6 : 1 }} onClick={() => void handleRun()} disabled={running || clearing}>
              {runLabel}
            </button>
            {/* Clear is offered from your own-account view and acts on the selected scope. */}
            {!isCross && scopeMigratedCount > 0 && !confirmClear && (
              <button style={styles.linkBtn} onClick={() => setConfirmClear(true)} disabled={running || clearing}>
                Clear migrated {clearTargetLabel}
              </button>
            )}
          </div>

          {confirmClear && (
            <div style={styles.confirmBox}>
              <p style={styles.confirmText}>
                Remove the migrated <strong>{clearTargetLabel}</strong> from ClearIntake for this
                account? Anything you created yourself is kept, and seeUwell is untouched. You can
                re-migrate anytime.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...styles.dangerBtn, opacity: clearing ? 0.6 : 1 }} onClick={() => void handleClear()} disabled={clearing}>
                  {clearing ? 'Clearing…' : 'Yes, clear it'}
                </button>
                <button style={styles.cancelBtn} onClick={() => setConfirmClear(false)} disabled={clearing}>Cancel</button>
              </div>
            </div>
          )}
        </>
      )}

      {error && <div style={styles.errorBanner}>{error}</div>}
      {msg   && <div style={styles.successBanner}>{msg}</div>}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '20px 24px' },
  cardTitle: { margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#111827' },
  help: { fontSize: 13, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.5 },
  muted: { fontSize: 13, color: '#9ca3af', margin: 0 },
  sourceBox: { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 16 },
  sourceLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 },
  sourceRow: { display: 'flex', alignItems: 'center', gap: 8 },
  sourceInput: { flex: 1, border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, color: '#111827', boxSizing: 'border-box' },
  sourceNote: { fontSize: 12, color: '#6b7280', margin: '8px 0 0' },
  segment: { display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden', marginBottom: 16 },
  segBtn: { background: '#fff', border: 'none', borderRight: '1px solid #e5e7eb', padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: '#374151' },
  segBtnActive: { background: '#2563eb', color: '#fff' },
  statRow: { display: 'flex', gap: 12, marginBottom: 16 },
  stat: { flex: 1, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 2 },
  statNum: { fontSize: 22, fontWeight: 700, color: '#111827' },
  statTotal: { fontSize: 14, fontWeight: 500, color: '#9ca3af' },
  statLabel: { fontSize: 12, color: '#6b7280' },
  btnRow: { display: 'flex', alignItems: 'center', gap: 14 },
  primaryBtn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, padding: '9px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  secondaryBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' },
  linkBtn: { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, fontWeight: 500, textDecoration: 'underline', padding: 0, whiteSpace: 'nowrap' },
  confirmBox: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 16, marginTop: 14 },
  confirmText: { fontSize: 13, color: '#7f1d1d', margin: '0 0 12px', lineHeight: 1.5 },
  dangerBtn: { background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  cancelBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  errorBanner: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '10px 16px', color: '#b91c1c', fontSize: 13, marginTop: 14 },
  successBanner: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '10px 16px', color: '#166534', fontSize: 13, marginTop: 14 },
};
